import React, {useMemo, useState} from 'react';
import {Avatar, Col, Dropdown, message, Modal, Row, Typography} from 'antd';
import {
    DollarCircleOutlined,
    FundOutlined,
    GlobalOutlined,
    IdcardOutlined,
    LineChartOutlined,
    LogoutOutlined,
    PictureOutlined,
    StockOutlined,
    SwapOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';

const {Text} = Typography;

// —— helpers
const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
};

// Brand palette (from your project)
const BRAND = {
    primary: '#4b49ac',
    light: '#98bdff',
    accent: '#f3797e',
    white: '#ffffff',
};

const AVATAR_OPTIONS = [
    // 💹 کندل‌استیک / مارکت
    {
        key: 'candles-primary',
        labelKey: 'profile_menu.avatar.candles', // مثلا: "نمودار شمعی فارکس"
        bg: BRAND.primary,
        icon: <StockOutlined />,
    },
    // 📊 صندوق / حساب سرمایه‌گذاری
    {
        key: 'fund-primary',
        labelKey: 'profile_menu.avatar.fund', // مثلا: "حساب سرمایه‌گذاری"
        bg: BRAND.primary,
        icon: <FundOutlined />,
    },
    // 💵 دلار / بالانس
    {
        key: 'dollar-primary',
        labelKey: 'profile_menu.avatar.dollar', // مثلا: "بالانس و سرمایه"
        bg: BRAND.primary,
        icon: <DollarCircleOutlined />,
    },
    // 🔁 سوآپ / پوزیشن‌ها
    {
        key: 'swap-primary',
        labelKey: 'profile_menu.avatar.swap', // مثلا: "معاملات باز"
        bg: BRAND.primary,
        icon: <SwapOutlined />,
    },

    // 🌍 مارکت جهانی / CFD
    {
        key: 'world-light',
        labelKey: 'profile_menu.avatar.world', // مثلا: "مارکت‌های جهانی"
        bg: BRAND.light,
        icon: <GlobalOutlined />,
    },
    // 📈 چارت لاین / آنالیز
    {
        key: 'chart-light',
        labelKey: 'profile_menu.avatar.chart', // مثلا: "آنالیز نموداری"
        bg: BRAND.light,
        icon: <LineChartOutlined />,
    },
    // ⚡ ریسک / نوسان
    {
        key: 'risk-accent',
        labelKey: 'profile_menu.avatar.risk', // مثلا: "تریدر پرریسک"
        bg: BRAND.accent,
        icon: <ThunderboltOutlined />,
    },
    // 👤 پروفایل تریدر
    {
        key: 'trader-accent',
        labelKey: 'profile_menu.avatar.trader', // مثلا: "پروفایل تریدر"
        bg: BRAND.accent,
        icon: <UserOutlined />,
    },
];

// Render an icon avatar preview (for grid and header)
const IconAvatar = ({bg, size = 72, icon, invertColor = true}) => (
    <div
        style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: bg,
            color: invertColor ? '#fff' : '#111',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: Math.round(size * 0.45),
            lineHeight: 1,
        }}
    >
        {icon}
    </div>
);

const ProfileMenu = ({
                         user,                 // { fullName, phoneNumber, avatarUrl }  (avatarUrl kept for backward compatibility)
                         onLogout,
                         onGoProfile,
                         onAvatarChange,       // (val) => persist to backend if needed. val is either URL or "icon:<key>"
                         size = 32,
                     }) => {
    const {t} = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);

    // Support both URL-based avatar (old) and icon-key avatar (new)
    const storedKey = localStorage.getItem('avatar_key') || null;
    const storedUrl = localStorage.getItem('avatar_url') || null;

    // live avatar: prefer prop -> stored URL -> stored key -> undefined
    const liveUrl = user?.avatarUrl || storedUrl || undefined;
    const liveKey = storedKey && !liveUrl ? storedKey : null; // only use key if there is no URL
    const initials = getInitials(user?.fullName || user?.email || '');

    const headerAvatar = useMemo(() => {
        if (liveUrl) return {type: 'url', value: liveUrl};
        if (liveKey) return {type: 'icon', value: liveKey};
        return {type: 'none', value: null};
    }, [liveUrl, liveKey]);

    const handlePickIcon = (opt) => {
        // Persist icon selection
        localStorage.setItem('avatar_key', opt.key);
        localStorage.removeItem('avatar_url'); // switch to icon mode
        if (onAvatarChange) onAvatarChange(`icon:${opt.key}`);
        message.success(t('profile_menu.avatar_saved'));
        setPickerOpen(false);
    };

    const truncateMiddle = (value: string, max = 18) => {
        if (!value) return "";
        if (value.length <= max) return value;

        const keep = max - 3; // برای "..."
        const left = Math.ceil(keep / 2);
        const right = Math.floor(keep / 2);

        return value.slice(0, left) + "..." + value.slice(-right);
    };

    const menuItems = [
        {
            key: 'header',
            type: 'group',
            label: (
                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                    {headerAvatar.type === 'icon' ? (
                        <IconAvatar
                            size={40}
                            bg={AVATAR_OPTIONS.find(a => a.key === headerAvatar.value)?.bg || BRAND.primary}
                            icon={AVATAR_OPTIONS.find(a => a.key === headerAvatar.value)?.icon || <UserOutlined/>}
                        />
                    ) : (
                        <Avatar size={40} src={headerAvatar.type === 'url' ? headerAvatar.value : undefined}
                                icon={headerAvatar.type !== 'url' && <UserOutlined/>}>
                            {headerAvatar.type === 'none' && initials}
                        </Avatar>
                    )}

                    <div style={{minWidth: 0}}>
                        <Text strong style={{display: 'block'}} ellipsis>
                            {user?.fullName || t('profile_menu.user')}
                        </Text>
                        <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                            title={user?.email || ""} // برای نمایش کامل روی hover
                        >
                            {truncateMiddle(user?.email || "")}
                        </Text>
                    </div>
                </div>
            ),
        },
        {type: 'divider'},
        {key: 'profile', icon: <IdcardOutlined/>, label: t('profile_menu.my_profile'), onClick: onGoProfile},
        // { key: 'settings', icon: <SettingOutlined />, label: t('profile_menu.advanced_settings'), onClick: onOpenSettings },
        {
            key: 'avatar',
            icon: <PictureOutlined/>,
            label: t('profile_menu.change_avatar'),
            onClick: () => setPickerOpen(true),
        },
        {type: 'divider'},
        {key: 'logout', danger: true, icon: <LogoutOutlined/>, label: t('profile_menu.logout'), onClick: onLogout},
    ];

    return (
        <>
            <Dropdown trigger={['click']} placement="bottomRight" menu={{items: menuItems}}>
                {/* header trigger avatar */}
                {headerAvatar.type === 'icon' ? (
                    <div style={{cursor: 'pointer'}}>
                        <IconAvatar
                            size={size}
                            bg={AVATAR_OPTIONS.find(a => a.key === headerAvatar.value)?.bg || BRAND.primary}
                            icon={AVATAR_OPTIONS.find(a => a.key === headerAvatar.value)?.icon || <UserOutlined/>}
                        />
                    </div>
                ) : (
                    <Avatar
                        size={size}
                        src={headerAvatar.type === 'url' ? headerAvatar.value : undefined}
                        icon={headerAvatar.type !== 'url' && <UserOutlined/>}
                        style={{cursor: 'pointer'}}
                    >
                        {headerAvatar.type === 'none' && initials}
                    </Avatar>
                )}
            </Dropdown>

            {/* Avatar picker modal */}
            <Modal
                open={pickerOpen}
                title={t('profile_menu.select_avatar_title')}
                onCancel={() => setPickerOpen(false)}
                footer={null}
                width={680}
                bodyStyle={{padding: 16}}
            >
                <Row gutter={[16, 16]}>
                    {AVATAR_OPTIONS.map((opt) => (
                        <Col key={opt.key} xs={12} sm={12} md={8} lg={6} xl={6}>
                            <button
                                type="button"
                                onClick={() => handlePickIcon(opt)}
                                style={{
                                    // CARD
                                    width: '100%',
                                    height: 168,                   // same height = aligned rows
                                    border: '1px solid #eaeaea',
                                    borderRadius: 16,
                                    background: "var(--npe-surface)",
                                    cursor: 'pointer',
                                    padding: 16,
                                    // LAYOUT
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    // EFFECTS
                                    transition: 'box-shadow .15s ease, transform .15s ease, border-color .15s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.08)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.borderColor = '#dcdcdc';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'none';
                                    e.currentTarget.style.borderColor = '#eaeaea';
                                }}
                            >
                                {/* ICON CIRCLE */}
                                <div
                                    style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '50%',
                                        background: opt.bg,
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 32,              // big icon
                                        lineHeight: 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    {opt.icon}
                                </div>

                                {/* LABEL + ACTION */}
                                <div style={{textAlign: 'center'}}>
                                    <div style={{fontSize: 14, color: '#3a3a3a', marginTop: 8}}>
                                        {t(opt.labelKey)}
                                    </div>
                                </div>
                            </button>
                        </Col>
                    ))}
                </Row>
            </Modal>
        </>
    );
};

export default ProfileMenu;
